import { useEffect, useSyncExternalStore } from 'react'
import { useLocation, useMatches, useNavigate } from 'react-router-dom'
import {
  identityApi,
  selectSessionStatus,
  verifiedProfile,
} from '../../features/identity'
import {
  StoriesHost,
  type StoryNavigationType,
} from '../../features/engagement'
import { browserTokenStorage } from '../../platform/auth/browser-token-storage'
import { useAppDispatch, useAppSelector } from '../store/hooks'

export function StoriesIntegration({
  navigationType,
}: {
  navigationType: (path: string) => StoryNavigationType
}) {
  const dispatch = useAppDispatch()
  const status = useAppSelector(selectSessionStatus)
  const token = useSyncExternalStore(
    browserTokenStorage.subscribe,
    browserTokenStorage.read,
  )
  const verified = useSyncExternalStore(
    verifiedProfile.subscribe,
    verifiedProfile.read,
  )
  const matches = useMatches()
  const { pathname, key: navigationKey } = useLocation()
  const navigate = useNavigate()
  const handles = matches
    .map((match) => match.handle)
    .filter(
      (handle): handle is Record<string, unknown> =>
        typeof handle === 'object' && handle !== null,
    )
  const inCabinet =
    status === 'authenticated' &&
    (pathname === '/' || handles.some((handle) => handle.personalArea === true))
  const allowed =
    handles.some((handle) => handle.storyAutoDisplay === 'allow') &&
    !handles.some(
      (handle) => 'studyMode' in handle || handle.immersiveLearning === true,
    )

  useEffect(() => {
    if (status !== 'authenticated' || !token) return
    let current = true
    const ensureVerified = async () => {
      // Joining a pre-existing request is normal. If it belonged to the old
      // token, fetch once more after it settles; its profile cannot verify us.
      for (let attempt = 0; attempt < 2; attempt += 1) {
        if (
          !current ||
          browserTokenStorage.read() !== token ||
          verifiedProfile.matches(token)
        )
          return
        await dispatch(
          identityApi.endpoints.getCurrentUser.initiate(undefined, {
            forceRefetch: true,
            subscribe: false,
          }),
        )
      }
    }
    void ensureVerified()
    return () => {
      current = false
    }
  }, [dispatch, status, token, verified])

  if (
    status !== 'authenticated' ||
    !token ||
    !verified ||
    !verifiedProfile.matches(token)
  )
    return null
  return (
    <StoriesHost
      key={verified.generation}
      accessToken={token}
      account={String(verified.userId)}
      navigationKey={navigationKey}
      allowed={allowed}
      manualAllowed={inCabinet}
      inCabinet={inCabinet}
      navigate={(path, type) => {
        if (type === 'document') window.location.assign(path)
        else void navigate(path)
      }}
      navigationType={navigationType}
    />
  )
}
